
















































































































import Vue, { PropType } from 'vue';
import Component, { mixins } from 'vue-class-component';
import canAutoplay, { CheckResponse } from 'can-autoplay';
import PlyrType, { Options } from 'plyr';
import { Watch } from 'vue-property-decorator';
import ResponsiveImage, { ResponsiveImageInterface } from '@/common/vue/ResponsiveImage.vue';
import VideoPauseHandler from '@/common/ts/pausing-videos/VideoPauseHandler';
import ResizeUpdateMixin, { MOBILE_WIDTH, TABLET_WIDTH } from '../ts/resizing/ResizeUpdateMixin';
import IntersectionDirective from './intersection-directive';

const IconPlay = () => import('../icons/play.svg');
const IconClose = () => import('../icons/close.svg');

export const VIDEO_PLAY_EVENT = 'brandhub-video-playback';
export const VIDEO_PAUSE_EVENT = 'brandhub-video-pause';
export const VIDEO_CLOSE_EVENT = 'brandhub-video-close';
export const VIDEO_PLAYBACK_ENDED_EVENT = 'playback-ended';
export const VIDEO_CLOSE_FULLSCREEN_EVENT = 'close-fullscreen';

const VideoPlayerProps = Vue.extend({
  props: {
    showControls: {
      type: Boolean,
      default: true,
    },
    showControlsNative: Boolean,
    isPlaying: Boolean,
    playMuted: Boolean,
    autoPlay: Boolean,
    playLooped: Boolean,
    playOnHover: Boolean,
    playOnMount: Boolean,
    playOnIntersect: Boolean,
    preloadVideo: String,
    desktopVideo: String,
    tabletVideo: String,
    mobileVideo: String,
    posterImage: String,
    tabindex: String,
    posterImageObject: Object as PropType<ResponsiveImageInterface>,
    withOverlay: Boolean,
    videoLength: String,
  },
});

@Component({
  directives: {
    intersect: IntersectionDirective,
  },
  components: {
    ResponsiveImage,
    IconPlay,
    IconClose,
  },
})
export default class VideoPlayer extends mixins(VideoPlayerProps, ResizeUpdateMixin) {
  $refs!: {
    videoPlayer: HTMLVideoElement;
    image: ResponsiveImage;
  }

  videoPlayer: PlyrType | null = null;
  plyrOptions: Options = {
    loadSprite: false,
    controls: [],
    hideControls: !this.showControls,
    muted: this.startMuted,
  };

  showPosterImage = false;
  showVideo = false;
  showOverlay = !this.playOnIntersect;

  @Watch('isPlaying')
  onPlaybackChanged (value: boolean) {
    if (value) {
      this.play();
    } else {
      this.pause();
    }
  }

  async play (fromStart = false) {
    if (this.$refs.videoPlayer) {
      await this.handleBlockingBrowsers();
      if (fromStart) {
        this.$refs.videoPlayer.currentTime = 0;
      }
      await this.$refs.videoPlayer.play();
    }
  }

  pause () {
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.pause();
    }
  }

  handleMouseEnter () {
    if (this.playOnHover) {
      this.play();
    }
  }

  handleMouseLeave () {
    if (this.playOnHover) {
      this.pause();
    }
  }

  handleIntersectChange (isIntersecting: boolean) {
    if (isIntersecting && (this.playOnIntersect || this.autoPlay)) {
      this.play();
    } else if (!isIntersecting) {
      this.pause();
    }
  }

  dispatchPlayEvent () {
    if (!this.autoPlay && !this.playOnHover && !this.playOnIntersect) {
      window.dispatchEvent(new CustomEvent<HTMLVideoElement>(VIDEO_PLAY_EVENT, {
        detail: this.$refs.videoPlayer,
      }));
    }
  }

  dispatchPauseEvent () {
    this.$el.dispatchEvent(new CustomEvent(VIDEO_PAUSE_EVENT, {
      bubbles: true,
      composed: true,
      detail: this,
    }));
  }

  endedPlaying () {
    this.$emit(VIDEO_PLAYBACK_ENDED_EVENT);
  }

  pauseVideo (event: any) {
    if (!this.autoPlay && event && this.videoPlayer && this.$refs.videoPlayer !== event.detail) {
      this.pause();
    }
  }

  muteVideo () {
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.muted = true;
    }
  }

  unmuteVideo () {
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.muted = false;
    }
  }

  onPlayButtonClick () {
    this.$el.dispatchEvent(new CustomEvent(VIDEO_PLAY_EVENT, {
      bubbles: true,
      composed: true,
      detail: this.$refs.videoPlayer,
    }));
    this.showOverlay = false;
    this.play();
  }

  onCloseButtonClick () {
    this.$el.dispatchEvent(new CustomEvent(VIDEO_CLOSE_EVENT, {
      bubbles: true,
      composed: true,
      detail: this.$refs.videoPlayer,
    }));
    this.showOverlay = true;
    this.pause();
  }

  get videoSource () {
    if (this.mobileVideo && this.isMobile) {
      return this.mobileVideo;
    }
    if (this.tabletVideo && this.isTablet) {
      return this.tabletVideo;
    }
    if (this.desktopVideo) {
      return this.desktopVideo;
    }

    return '';
  }

  get startMuted () {
    return this.playMuted
      || this.autoPlay
      || this.playOnHover
      || this.playOnIntersect;
  }

  async canAutoplay (): Promise<CheckResponse> {
    return Promise.resolve()
      .then(() => new Promise((resolve) => setTimeout(() => resolve(), 500)))
      .then(() => canAutoplay.video({ inline: true, muted: true }));
  }

  async handleBlockingBrowsers () {
    if (!this.posterImage && !this.posterImageObject) {
      this.showVideo = true;
      return;
    }
    if (!this.videoSource) {
      this.showVideo = false;
      this.showPosterImage = true;
      this.$refs.image.loadingTriggered = true;
      return;
    }
    this.canAutoplay().then(({ error }) => {
      if (error && error.name === 'NotAllowedError') {
        this.showPosterImage = true;
        this.$refs.image.loadingTriggered = true;
      } else {
        this.showVideo = true;
        this.showPosterImage = false;
      }
    });
  }

  mounted () {
    window.addEventListener(VIDEO_PLAY_EVENT, this.pauseVideo);

    VideoPauseHandler.getInstance().addNewVideo(this.$refs.videoPlayer);

    this.handleBlockingBrowsers();

    if (this.showControls) {
      this.plyrOptions.controls = [
        'play', // Play/pause playback
        'progress', // The progress bar and scrubber for playback and buffering
        'current-time', // The current time of playback
        'duration', // The full duration of the media
        'mute', // Toggle mute
        'volume', // Volume control
        'fullscreen',
      ];
    }

    if (this.playOnMount) {
      this.play();
    }

    if (window.innerWidth <= MOBILE_WIDTH) {
      this.$refs.videoPlayer.addEventListener('webkitendfullscreen', () => {
        this.$emit(VIDEO_CLOSE_FULLSCREEN_EVENT);
      }, { passive: true });
    }

    if (this.showControls) {
      const load = () => import('plyr').then((module) => {
        const Plyr = module.default;
        this.videoPlayer = new Plyr(this.$refs.videoPlayer, this.plyrOptions);
      });
      load();
    }
  }

  beforeDestroy () {
    window.removeEventListener(VIDEO_PLAY_EVENT, this.pauseVideo);
  }
}
